import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, LogOut, Monitor, Smartphone, Tablet, ShieldCheck, RefreshCw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { parseUserAgent, relTime, ageBetween } from "@/utils/userAgent";

function DeviceIcon({ device }) {
    if (/iPhone|Android phone/.test(device)) return <Smartphone size={14} strokeWidth={1.75} />;
    if (/iPad|tablet/.test(device))          return <Tablet size={14} strokeWidth={1.75} />;
    return <Monitor size={14} strokeWidth={1.75} />;
}

export default function SessionsCard({ userId, onConfirm }) {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setSessions(await api.adminListUserSessions(userId));
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load sessions");
            setSessions([]);
        } finally { setLoading(false); }
    }, [userId]);

    useEffect(() => { load(); }, [load]);

    async function revokeOne(sid) {
        setBusy(`one:${sid}`);
        try {
            await api.adminRevokeUserSession(userId, sid);
            toast.success("Session revoked");
            await load();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Revoke failed");
        } finally { setBusy(null); }
    }

    function askRevokeOthers() {
        const otherCount = sessions.filter((s) => !s.is_current).length;
        if (otherCount === 0) {
            toast.info("There are no other active sessions to revoke.");
            return;
        }
        onConfirm({
            kind: "revokeOthers",
            label: `Revoke all other active sessions for this user? ${otherCount} session${otherCount === 1 ? "" : "s"} will be signed out immediately. The current session is preserved.`,
            run: async () => {
                await api.adminRevokeOtherUserSessions(userId);
                toast.success("Other sessions revoked");
                await load();
            },
        });
    }

    return (
        <div className="bg-white rounded-xl border border-[#E5E7EB]" data-testid="admin-sec-sessions">
            <div className="flex items-center justify-between px-4 md:px-5 py-3 border-b border-[#E5E7EB]">
                <div className="flex items-center gap-2">
                    <div className="text-sm font-semibold text-[#131B2F]">Active sessions</div>
                    <span className="text-xs text-[#4B5565]">{sessions.length}</span>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={load}
                        className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md border border-[#E5E7EB] bg-white text-xs text-[#131B2F] hover:bg-[#F5F5F7]"
                        title="Reload"
                        data-testid="admin-sec-sessions-reload"
                    >
                        <RefreshCw size={12} strokeWidth={1.75} />
                    </button>
                    <button
                        type="button"
                        onClick={askRevokeOthers}
                        disabled={sessions.filter((s) => !s.is_current).length === 0}
                        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-[#131B2F] hover:bg-[#0B1220] text-white text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="admin-sec-sessions-revoke-others"
                    >
                        <LogOut size={12} strokeWidth={1.75} /> Revoke all others
                    </button>
                </div>
            </div>

            <div>
                {loading ? (
                    <div className="p-6 text-center text-xs text-[#4B5565]"><Loader2 size={14} className="inline animate-spin" /> Loading…</div>
                ) : sessions.length === 0 ? (
                    <div className="p-6 text-center text-xs text-[#4B5565]">No active sessions.</div>
                ) : (
                    <ul className="divide-y divide-[#F0F1F5]">
                        {sessions.map((s) => {
                            const ua = parseUserAgent(s.user_agent);
                            const isBusy = busy === `one:${s.id}`;
                            return (
                                <li key={s.id} className="px-4 md:px-5 py-3 flex items-start gap-3" data-testid={`admin-sec-session-${s.id}`}>
                                    <div className="w-8 h-8 rounded-md bg-[#F5F5F7] flex items-center justify-center text-[#131B2F] shrink-0">
                                        <DeviceIcon device={ua.device} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <div className="text-sm font-medium text-[#131B2F]">
                                                {ua.browser}
                                            </div>
                                            <span className="text-[11px] text-[#4B5565]">on {ua.platform}</span>
                                            {s.is_current && (
                                                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] text-[#2E7E22] bg-[#E6F1DE] px-1.5 py-0.5 rounded" data-testid={`admin-sec-session-${s.id}-current`}>
                                                    <ShieldCheck size={10} /> Current
                                                </span>
                                            )}
                                        </div>
                                        <div className="mt-0.5 text-[11px] text-[#4B5565] flex flex-wrap items-center gap-x-3 gap-y-0.5">
                                            <span>Device: {ua.device}</span>
                                            {s.ip && <span>IP {s.ip}</span>}
                                            <span title={s.last_seen_at || s.created_at}>Last active {relTime(s.last_seen_at || s.created_at)}</span>
                                            <span>Age {ageBetween(s.created_at)}</span>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => revokeOne(s.id)}
                                        disabled={s.is_current || isBusy}
                                        title={s.is_current ? "Cannot revoke your current session — sign out instead" : "Revoke this session"}
                                        className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md border border-[#DA3535]/30 text-[#991B1B] bg-white text-[11px] font-medium hover:bg-[#DA3535]/8 disabled:opacity-40 disabled:cursor-not-allowed"
                                        data-testid={`admin-sec-session-revoke-${s.id}`}
                                    >
                                        {isBusy ? <Loader2 size={12} className="animate-spin" /> : <LogOut size={12} />}
                                        Revoke
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
}
